import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const TermsPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 2, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="terms-page">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-28 pb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1C3A7A] mb-2">Terms and Conditions</h1>
        <p className="text-gray-500 mb-8">Last Updated: {lastUpdated}</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">1. Introduction</h2>
            <p>
              Welcome to RecoveryMatch ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your 
              access to and use of the RecoveryMatch mobile application and website (collectively, the "Service"). 
              By creating an account or using our Service, you agree to be bound by these Terms.
            </p>
            <p className="mt-3">
              RecoveryMatch is a dating platform designed specifically for individuals in recovery from alcohol 
              and substance use disorders. Our mission is to help people in recovery find meaningful romantic 
              connections with others who understand their journey.
            </p>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">2. Eligibility Requirements</h2>
            <p>To use RecoveryMatch, you must:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Be at least 18 years of age.</strong> RecoveryMatch is exclusively for adults. By using our Service, you represent and warrant that you are at least 18 years old.</li>
              <li>Have the legal capacity to enter into a binding agreement.</li>
              <li>Not be prohibited from using the Service under applicable laws.</li>
              <li>Not have been previously banned or removed from RecoveryMatch.</li>
              <li>Not be a registered sex offender.</li>
            </ul>
            <p className="mt-3">
              We reserve the right to request proof of age at any time and to terminate accounts that violate 
              this age requirement.
            </p>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">3. Account Registration and Security</h2>
            <p>
              To access certain features of RecoveryMatch, you must create an account. When registering, you agree to:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Provide accurate, current, and complete information.</li>
              <li>Maintain and promptly update your account information.</li>
              <li>Keep your password secure and confidential.</li>
              <li>Notify us immediately of any unauthorized access to your account.</li>
              <li>Accept responsibility for all activities that occur under your account.</li>
            </ul>
            <p className="mt-3">
              You may not create multiple accounts, share your account with others, or transfer your account 
              to another person.
            </p>
          </section>

          {/* User Conduct */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">4. User Conduct and Community Guidelines</h2>
            <p>RecoveryMatch is committed to maintaining a safe, supportive environment. You agree NOT to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Post false, misleading, or deceptive information in your profile.</li>
              <li>Harass, bully, stalk, intimidate, or threaten other users.</li>
              <li>Send unsolicited sexual content or messages.</li>
              <li>Promote or encourage substance use or abuse.</li>
              <li>Share content that glorifies drugs, alcohol, or addictive behaviors.</li>
              <li>Discriminate against others based on race, ethnicity, religion, gender, sexual orientation, or disability.</li>
              <li>Use the Service for commercial purposes, advertising, or solicitation.</li>
              <li>Impersonate another person or entity.</li>
              <li>Upload viruses, malware, or other harmful code.</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts.</li>
              <li>Use automated systems (bots) to interact with the Service.</li>
            </ul>
            <p className="mt-3">
              We reserve the right to investigate and take appropriate action against anyone who violates these 
              guidelines, including removing content, suspending accounts, or reporting to law enforcement.
            </p>
          </section>

          {/* Content */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">5. User Content</h2>
            <p>
              You retain ownership of the content you post on RecoveryMatch (photos, profile information, messages). 
              However, by posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, 
              reproduce, modify, and display that content in connection with operating and improving the Service.
            </p>
            <p className="mt-3">
              You are solely responsible for the content you post and represent that you have all necessary 
              rights to share such content. Content that violates our Community Guidelines or these Terms may 
              be removed without notice.
            </p>
          </section>

          {/* Subscriptions */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">6. Subscriptions and Payments</h2>
            <p>
              RecoveryMatch offers free and premium subscription tiers (Recovery+ and Recovery Elite). 
              By purchasing a subscription:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>You authorize us to charge your chosen payment method on a recurring basis.</li>
              <li>Subscriptions automatically renew unless cancelled at least 24 hours before the renewal date.</li>
              <li>Prices are subject to change with reasonable notice.</li>
              <li>Refunds are generally not provided for partial subscription periods.</li>
            </ul>
            <p className="mt-3">
              In-app purchases of virtual coins and gifts are non-refundable except where required by law.
            </p>
          </section>

          {/* Virtual Currency */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">7. Virtual Currency and Gifts</h2>
            <p>
              RecoveryMatch features a virtual currency system ("Coins") that can be used to purchase virtual 
              gifts and unlock certain features. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Coins have no real-world monetary value and cannot be exchanged for cash.</li>
              <li>Virtual gifts are digital items with no intrinsic value.</li>
              <li>We may modify, discontinue, or reset virtual currency at any time.</li>
              <li>Unused Coins are not refundable.</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">8. Account Termination</h2>
            <p>
              You may delete your account at any time through the app settings. We may suspend or terminate 
              your account if:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>You violate these Terms or our Community Guidelines.</li>
              <li>We are required to do so by law.</li>
              <li>We reasonably believe your conduct may harm other users or our business.</li>
            </ul>
            <p className="mt-3">
              Upon termination, your right to use the Service immediately ceases. We may retain certain 
              data as required by law or for legitimate business purposes.
            </p>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">9. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT 
              GUARANTEE THAT YOU WILL FIND A MATCH OR THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
            </p>
            <p className="mt-3">
              RecoveryMatch does not conduct criminal background checks on users. We are not responsible for 
              the conduct of any user, whether online or offline. You are solely responsible for your 
              interactions with other users.
            </p>
            <p className="mt-3 font-semibold">
              RecoveryMatch is not a medical service and does not provide addiction treatment, counseling, 
              or medical advice. If you are struggling with substance use, please contact a qualified 
              healthcare provider or call SAMHSA's National Helpline at 1-800-662-4357.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, RECOVERYMATCH SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS 
              OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
            </p>
            <p className="mt-3">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS 
              PRECEDING THE CLAIM.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">11. Governing Law and Disputes</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States. 
              Any disputes arising from these Terms or your use of the Service shall be resolved through 
              binding arbitration, except where prohibited by law.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">12. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes by posting 
              the updated Terms on our Service and updating the "Last Updated" date. Your continued use of 
              the Service after such changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">13. Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="mt-3">
              <strong>Email:</strong> support@recoverymatch.com<br />
              <strong>Address:</strong> RecoveryMatch Inc., [Your Address]
            </p>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button 
            onClick={() => navigate("/")}
            className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition"
          >
            ← Back to Home
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
